import PropTypes from 'prop-types'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import {
  completeStep,
  decrementCurrentStep,
  incrementCurrentStep,
  incrementCurrentStepBy,
  selectAcceptedMaterialsIds,
  validProgramDetailsStep,
  validProgramMaterialsStep,
} from '../../../reducers/programRegistrationReducer'
import { initializeSession, requestEnrollment } from '../../../actions/programRegistrationActions'
import { getSignUpPageProgram } from '../../../actions/programActions'

import { Col, Container, Row } from 'reactstrap'
import ConfirmationModal2 from '../../common/ConfirmationModal2'
import SubmitButton from '../../common/buttons/SubmitButton'

import './ProgramRegistrationFooter.scss'

const ProgramRegistrationFooter = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [searchParams] = useSearchParams()

  const course = useSelector(state => state.programPresentation.course)
  const {
    status,
    currentStep,
    steps,
    completedSteps,
    selectedFamilyMember,
    selectedPackage,
    selectedPaymentMethod,
  } = useSelector(state => state.programRegistration)
  const acceptedMaterialsIds = useSelector(selectAcceptedMaterialsIds)
  const validFirstStep = useSelector(validProgramDetailsStep)
  const validSecondStep = useSelector(validProgramMaterialsStep)

  const [showCancelConfirmationModal, setShowCancelConfirmationModal] = useState(false)

  const isLoading = status === 'processing registration'
  const isValidStep = useMemo(() => {
    switch (currentStep) {
      case 0:
        return validFirstStep
      case 1:
        if (steps[1] === 'programMaterials') {
          return validSecondStep
        } else {
          return true
        }
      default:
        return true
    }
  }, [currentStep, steps, validFirstStep, validSecondStep])

  const handleGoBack = () => {
    if (currentStep === 0) {
      setShowCancelConfirmationModal(true)
    } else {
      dispatch(decrementCurrentStep())
    }
  }

  const goToCheckout = () => {
    selectedPaymentMethod === 'cash'
      ? dispatch(requestEnrollment({
        courseId: course.id,
        userId: selectedFamilyMember?.id,
        packageId: selectedPackage.id,
        packagePrice: selectedPackage.price,
        materialsIds: acceptedMaterialsIds,
        invitationToken: searchParams.get('invitation_token'),
      })).unwrap()
        .then(data => {
          navigate(`/landing/${course.community.id}/class/${course.id}/registration/${data.enrollment.registration.id}`)
        })
      : dispatch(initializeSession({
        courseId: course.id,
        userId: selectedFamilyMember?.id,
        packageId: selectedPackage.id,
        packagePrice: selectedPackage.price,
        materialsIds: acceptedMaterialsIds,
        invitationToken: searchParams.get('invitation_token'),
      })).unwrap()
        .then(data => {
          window.location.replace(data.sessionUrl)
        })
  }

  const handleGoNext = () => {
    if (!completedSteps.includes(currentStep)) {
      dispatch(completeStep(currentStep))
    }
    if (steps[currentStep + 1] === 'checkout') {
      dispatch(incrementCurrentStepBy(2))
      dispatch(completeStep(currentStep + 1))
      goToCheckout()
    } else {
      dispatch(incrementCurrentStep())
    }
  }

  const navigateToClassPage = () => {
    dispatch(getSignUpPageProgram(course.id))
    navigate(`/landing/${course.community.id}/class/${course.id}`)
  }

  return (
    <Container id='ProgramRegistrationFooter' fluid>
      <Row className='h-100 flex-column-reverse flex-sm-row justify-content-center align-items-center'>
        <Col sm={6} className='go-back'>
          <SubmitButton
            noTopMargin
            onClick={handleGoBack}
            disabled={isLoading}
            buttonMessage={t('programs.registration.footer.goBack')}
            buttonColor='navy-button'
          />
        </Col>
        <Col sm={6} className='submit-register-button'>
          <SubmitButton
            noTopMargin
            disabled={!isValidStep || isLoading}
            onClick={handleGoNext}
            buttonMessage={t('programs.registration.footer.nextStep')}
          />
        </Col>

        <ConfirmationModal2
          showModal={showCancelConfirmationModal}
          closeModal={() => setShowCancelConfirmationModal(false)}
          onDeny={() => setShowCancelConfirmationModal(false)}
          denyMessage={t('common.no')}
          onProceed={navigateToClassPage}
          proceedMessage={t('common.yes')}
          icon={<i className='fa fa-question-circle' style={{ fontSize: '72px' }} />}
          title={t('programs.registration.footer.cancelTitle')}
          message={t('programs.registration.footer.cancelMessage')}
        />
      </Row>
    </Container>
  )
}

ProgramRegistrationFooter.propTypes = {
  fetchCourse: PropTypes.func,
}

export default ProgramRegistrationFooter
